import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Head from "../components/Head";
import auth from "../utils/auth";
import OrderHistory from "../components/OrderHistory";
import "../styles/AccountPage.css";
import { scrollToTop } from '../hooks/useScrollToTop';

const AccountPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [originalName, setOriginalName] = useState("");
  const [careOf, setCareOf] = useState("");
  const [address, setAddress] = useState("");
  const [region, setRegion] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("Sverige");
  const [originalDeliveryDetails, setOriginalDeliveryDetails] = useState({});
  const [activeTab, setActiveTab] = useState("profile");

  const nameRef = useRef(null);
  const careOfRef = useRef(null);
  const addressRef = useRef(null);
  const regionRef = useRef(null);
  const postalCodeRef = useRef(null);

  const truncateText = (text, element) => {
    if (!element) return text;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = window.getComputedStyle(element).font;

    let truncated = text;
    while (context.measureText(truncated + '...').width > element.offsetWidth - 20) {
      truncated = truncated.slice(0, -1);
      if (truncated.length <= 3) break;
    }

    return truncated === text ? text : truncated + '...';
  };

  const fetchUserData = useCallback(async () => {
    if (!auth.isAuthenticated()) {
      auth.logout();
      navigate("/logga-in");
      scrollToTop();
      return;
    }

    try {
      const userId = auth.getUserId();
      const role = auth.getUserRole();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${role}/${userId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.getToken()}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Misslyckades med att hämta användardata: ${response.status} ${response.statusText}`);
      }

      const userData = await response.json();
      setUser(userData);
      setName(userData.profile.name || "");
      setOriginalName(userData.profile.name || "");
      setCareOf(userData.profile.careOf || "");
      setAddress(userData.profile.address || "");
      setRegion(userData.profile.region || "");
      setPostalCode(userData.profile.postalCode || "");
      setCountry(userData.profile.country || "Sverige");
      setOriginalDeliveryDetails({
        careOf: userData.profile.careOf || "",
        address: userData.profile.address || "",
        region: userData.profile.region || "",
        postalCode: userData.profile.postalCode || "",
        country: userData.profile.country || "Sverige",
      });
    } catch (error) {
      setError(error.message || "Det gick inte att hämta din kontoinformation. Försök igen senare.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleLogout = (event) => {
    event.preventDefault();
    auth.logout();
    navigate('/logga-in');
    scrollToTop();
  };

  const validateName = (name) => {
    const nameRegex = /^[^0-9_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
    if (!nameRegex.test(name)) {
      return "Namnet måste vara mellan 2 och 50 tecken långt och får inte innehålla siffror eller specialtecken.";
    }
    return null;
  };

  const validateCareOf = (name) => {
    const nameRegex = /^[^0-9_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
    if (!nameRegex.test(name)) {
      return "C/O måste vara mellan 2 och 50 tecken långt och får inte innehålla siffror eller specialtecken.";
    }
    return null;
  };

  const validateAddress = (address) => {
    const nameRegex = /^[^_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
    if (!nameRegex.test(address)) {
      return "Adressen måste vara mellan 2 och 50 tecken långt och får inte innehålla specialtecken.";
    }
    return null;
  };

  const validateRegion = (region) => {
    const nameRegex = /^[^0-9_!¡?÷?¿/\\+=@#£$€%ˆ&*(){}|~<>;:[\]]{2,50}$/;
    if (!nameRegex.test(region)) {
      return "Ort/staden måste vara mellan 2 och 50 tecken långt och får inte innehålla siffror eller specialtecken.";
    }
    return null;
  };

  const validatePostalCode = (postalCode) => {
    const postalCodeRegex = /^(\d{3}\s?\d{2})$/;
    if (!postalCodeRegex.test(postalCode)) {
      return "Postkoden måste bestå av 5 siffror och kan ha ett mellanslag efter de första 3 siffrorna.";
    }
    return null;
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    setUpdateError(null);

    if (name === originalName) {
      setIsEditing(false);
      return;
    }

    const nameError = validateName(name);
    if (nameError) {
      setUpdateError(nameError);
      return;
    }

    try {
      const userId = auth.getUserId();
      const role = auth.getUserRole();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${role}/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.getToken()}`
          },
          body: JSON.stringify({ name })
        }
      );

      if (!response.ok) {
        throw new Error("Kunde inte uppdatera namnet. Försök igen senare.");
      }

      setIsEditing(false);
      fetchUserData();
    } catch (error) {
      setUpdateError(error.message);
    }
  };

  const handleUpdateDeliveryDetails = async (e) => {
    e.preventDefault();
    setUpdateError(null);

    if (
      careOf === originalDeliveryDetails.careOf &&
      address === originalDeliveryDetails.address &&
      region === originalDeliveryDetails.region &&
      postalCode === originalDeliveryDetails.postalCode &&
      country === originalDeliveryDetails.country
    ) {
      setIsEditingDelivery(false);
      return;
    }

    if (careOf) {
      const careOfError = validateCareOf(careOf);
      if (careOfError) {
        setUpdateError(careOfError);
        return;
      }
    }

    if (address) {
      const addressError = validateAddress(address);
      if (addressError) {
        setUpdateError(addressError);
        return;
      }
    }

    if (region) {
      const regionError = validateRegion(region);
      if (regionError) {
        setUpdateError(regionError);
        return;
      }
    }

    if (postalCode) {
      const postalCodeError = validatePostalCode(postalCode);
      if (postalCodeError) {
        setUpdateError(postalCodeError);
        return;
      }
    }

    try {
      const userId = auth.getUserId();
      const role = auth.getUserRole();

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${role}/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${auth.getToken()}`
          },
          body: JSON.stringify({ careOf, address, region, postalCode, country })
        }
      );

      if (!response.ok) {
        throw new Error("Kunde inte uppdatera leveransdetaljerna. Försök igen senare.");
      }

      setIsEditingDelivery(false);
      fetchUserData();
    } catch (error) {
      setUpdateError(error.message);
    }
  };

  const getUserTypeClass = (status) => {
    switch (status) {
      case "user":
        return "Användare";
      case "admin":
        return "Admin";
      default:
        return "";
    }
  };

  const startNameEditing = () => {
    setIsEditing(true);
    setIsEditingDelivery(false);
    setUpdateError(null);
  };

  const startDeliveryEditing = () => {
    setIsEditingDelivery(true);
    setIsEditing(false);
    setUpdateError(null);
  };

  const handleTabChange = (tab) => {
    if (isEditing) {
      setName(originalName);
      setIsEditing(false);
    }
    
    if (isEditingDelivery) {
      setCareOf(originalDeliveryDetails.careOf);
      setAddress(originalDeliveryDetails.address);
      setRegion(originalDeliveryDetails.region);
      setPostalCode(originalDeliveryDetails.postalCode);
      setCountry(originalDeliveryDetails.country);
      setIsEditingDelivery(false);
    }
    
    setUpdateError(null);
    setActiveTab(tab);
    scrollToTop();
  };

  if (isLoading) {
    return (
      <div className="accountpage-loading-container">
        <div className="accountpage-loading">Laddar kontoinformation...</div>
      </div>
    );
  }

  return (
    <div className="accountpage-page">
      <Head title="EssayWriter | Mitt konto" />
      <div className="accountpage-container">
        <h1 className="accountpage-title">Mitt konto</h1>
        {error && <p className="accountpage-error">{error}</p>}
        {updateError && <p className="accountpage-error">{updateError}</p>}
        
        <div className="accountpage-tabs">
          <button 
            className={`accountpage-tab ${activeTab === 'profile' ? 'accountpage-active' : ''}`}
            onClick={() => handleTabChange('profile')}
          >
            Profil
          </button>
          <button 
            className={`accountpage-tab ${activeTab === 'orders' ? 'accountpage-active' : ''}`}
            onClick={() => handleTabChange('orders')}
          >
            Ordrar
          </button>
        </div>

        {activeTab === 'profile' && user && (
          <div className="accountpage-info">
            <p className="accountpage-info-item">
              <span className="accountpage-info-label">Användar-ID:</span>
              <span className="accountpage-info-value">{user._id}</span>
            </p>
            <p className="accountpage-info-item">
              <span className="accountpage-info-label">Behörighet:</span>
              <span className="accountpage-info-value">{getUserTypeClass(user.role)}</span>
            </p>
            <p className="accountpage-info-item">
              <span className="accountpage-info-label">E-post:</span>
              <span className="accountpage-info-value">{user.email}</span>
            </p>
            <p className="accountpage-info-item">
              <span className="accountpage-info-label">Namn:</span>
              {isEditing ? (
                <form onSubmit={handleUpdateName}>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="accountpage-input"
                    ref={nameRef}
                  />
                  <button type="submit" className="accountpage-update-btn">
                    {name === originalName ? "Avbryt" : "Uppdatera"}
                  </button>
                </form>
              ) : (
                <>
                  <span className="accountpage-info-value" ref={nameRef}>
                    {truncateText(user.profile.name, nameRef.current)}
                  </span>
                  <button 
                    onClick={startNameEditing} 
                    className={`accountpage-edit-btn ${isEditingDelivery ? 'accountpage-edit-btn-disabled' : ''}`}
                    disabled={isEditingDelivery}
                  >
                    Redigera
                  </button>
                </>
              )}
            </p>
            <p className="accountpage-info-item">
              <span className="accountpage-info-label">Medlem sedan:</span>
              <span className="accountpage-info-value">{new Date(user.createdAt).toLocaleDateString("sv-SE")}</span>
            </p>
            <h2 className="accountpage-title">Leveransdetaljer</h2>
            <div>
              {isEditingDelivery ? (
                <form onSubmit={handleUpdateDeliveryDetails}>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">C/O:</span>
                    <input
                      type="text"
                      value={careOf}
                      onChange={(e) => setCareOf(e.target.value)}
                      placeholder="Ange C/O (valfritt)"
                      className="accountpage-input"
                      ref={careOfRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Adress:</span>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Ange din adress"
                      className="accountpage-input"
                      ref={addressRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Ort (stad):</span>
                    <input
                      type="text"
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      placeholder="Ange din ort / stad"
                      className="accountpage-input"
                      ref={regionRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Postkod:</span>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="Ange din postkod"
                      className="accountpage-input"
                      ref={postalCodeRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Land:</span>
                    <select
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="accountpage-input"
                    >
                      <option value="Sverige">Sverige</option>
                    </select>
                  </p>
                  <div className="accountpage-info-item">
                    <button
                      type="submit"
                      className="accountpage-update-btn"
                    >
                      {careOf === originalDeliveryDetails.careOf &&
                        address === originalDeliveryDetails.address &&
                        region === originalDeliveryDetails.region &&
                        postalCode === originalDeliveryDetails.postalCode &&
                        country === originalDeliveryDetails.country
                        ? "Avbryt"
                        : "Uppdatera leveransdetaljer"}
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">C/O:</span>
                    <input
                      type="text"
                      value={truncateText(careOf || "", careOfRef.current)}
                      placeholder="Ange C/O (valfritt)"
                      className="accountpage-input"
                      disabled
                      ref={careOfRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Adress:</span>
                    <input
                      type="text"
                      value={truncateText(address || "", addressRef.current)}
                      placeholder="Ange din adress"
                      className="accountpage-input"
                      disabled
                      ref={addressRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Ort (stad):</span>
                    <input
                      type="text"
                      value={truncateText(region || "", regionRef.current)}
                      placeholder="Ange din ort / stad"
                      className="accountpage-input"
                      disabled
                      ref={regionRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Postkod:</span>
                    <input
                      type="text"
                      value={truncateText(postalCode || "", postalCodeRef.current)}
                      placeholder="Ange din postkod"
                      className="accountpage-input"
                      disabled
                      ref={postalCodeRef}
                    />
                  </p>
                  <p className="accountpage-info-item">
                    <span className="accountpage-info-label">Land:</span>
                    <input
                      type="text"
                      value={country || "Sverige"}
                      placeholder="Ange ditt land"
                      className="accountpage-input"
                      disabled
                    />
                  </p>
                  <div className="accountpage-info-item">
                    <button
                      type="button"
                      onClick={startDeliveryEditing}
                      className={`accountpage-edit-btn ${isEditing ? 'accountpage-edit-btn-disabled' : ''}`}
                      disabled={isEditing}
                    >
                      Redigera
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="accountpage-orders">
            <OrderHistory />
          </div>
        )}

        <button className="accountpage-btn" onClick={handleLogout}>
          Logga ut
        </button>
      </div>
    </div>
  );
};

export default AccountPage;